import { imsGet, imsPost, imsPut } from "../api/api";

/**
 * Product Catalog service — IMS inventory module.
 * Endpoints: /saas/ims/product-catalog/*
 *
 * Service codes follow the same S/A/U convention as FSM/CRM/PSM.
 */

const SEARCH = "IMS01S";
const ADD = "IMS01A";
const UPDATE = "IMS01U";

const DEFAULT_PAGE = { page: 0, size: 50, sortBy: "productName", sortDir: "ASC" };

/** Share in-flight identical GETs (helps React Strict Mode double-mount). */
const inflightRequests = new Map();

function dedupeAsync(key, factory) {
  const existing = inflightRequests.get(key);
  if (existing) return existing;
  const promise = Promise.resolve()
    .then(factory)
    .finally(() => {
      if (inflightRequests.get(key) === promise) inflightRequests.delete(key);
    });
  inflightRequests.set(key, promise);
  return promise;
}

function unwrapData(response) {
  if (response == null) return response;
  if (response.data !== undefined) {
    // Nested envelope: { data: { data: ... } }
    if (
      response.data &&
      typeof response.data === "object" &&
      !Array.isArray(response.data) &&
      response.data.data !== undefined
    ) {
      return response.data.data;
    }
    return response.data;
  }
  return response;
}

function unwrapPage(response) {
  const data = unwrapData(response);
  if (Array.isArray(data)) {
    return { content: data, totalElements: data.length, totalPages: 1, number: 0, size: data.length };
  }
  if (data && Array.isArray(data.content)) {
    return data;
  }
  // Alternate Spring / custom page shapes
  if (data && Array.isArray(data.products)) {
    return {
      content: data.products,
      totalElements: data.totalElements ?? data.products.length,
      totalPages: data.totalPages ?? 1,
      number: data.number ?? data.pageNumber ?? 0,
      size: data.size ?? data.pageSize ?? data.products.length,
    };
  }
  if (data && Array.isArray(data.records)) {
    return {
      content: data.records,
      totalElements: data.totalElements ?? data.records.length,
      totalPages: data.totalPages ?? 1,
      number: data.number ?? 0,
      size: data.size ?? data.records.length,
    };
  }
  if (data && Array.isArray(data.items)) {
    return {
      content: data.items,
      totalElements: data.totalElements ?? data.items.length,
      totalPages: data.totalPages ?? 1,
      number: data.number ?? 0,
      size: data.size ?? data.items.length,
    };
  }
  if (data && Array.isArray(data.data)) {
    return {
      content: data.data,
      totalElements: data.totalElements ?? data.data.length,
      totalPages: data.totalPages ?? 1,
      number: data.number ?? 0,
      size: data.size ?? data.data.length,
    };
  }
  return { content: [], totalElements: 0, totalPages: 0, number: 0, size: 0 };
}

function unwrapItem(response) {
  const data = unwrapData(response);
  if (Array.isArray(data)) return data[0] || null;
  return data && typeof data === "object" ? data : null;
}

function extractCount(response) {
  const data = unwrapData(response);
  if (typeof data === "number") return data;
  if (typeof data?.count === "number") return data.count;
  if (typeof data?.total === "number") return data.total;
  if (typeof data?.totalElements === "number") return data.totalElements;
  const n = Number(data);
  return Number.isFinite(n) ? n : 0;
}

function toNumberOrUndefined(value) {
  if (value === "" || value === null || value === undefined) return undefined;
  const n = Number(value);
  return Number.isNaN(n) ? undefined : n;
}

function toNumberOrNull(value) {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

function firstDefined(...values) {
  for (const v of values) {
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return undefined;
}

/** UI label → API productStatus enum. Create and Update both accept Active|End_Of_Production|Discontinued. */
export function toApiProductStatus(uiStatus) {
  const raw = String(uiStatus || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  let key = "ACTIVE";
  if (raw === "end_of_production" || raw === "endofproduction") key = "END_OF_PRODUCTION";
  else if (raw === "discontinued" || raw === "inactive") key = "DISCONTINUED";
  else if (raw === "active") key = "ACTIVE";
  else {
    const upper = String(uiStatus || "").trim().toUpperCase().replace(/[\s-]+/g, "_");
    if (["ACTIVE", "END_OF_PRODUCTION", "DISCONTINUED"].includes(upper)) key = upper;
  }

  if (key === "END_OF_PRODUCTION") return "End_Of_Production";
  if (key === "DISCONTINUED") return "Discontinued";
  return "Active";
}

export function toUiProductStatus(apiStatus, isActive) {
  if (isActive === false || isActive === "false") return "Inactive";
  const raw = String(apiStatus || "").trim().toUpperCase().replace(/[\s-]+/g, "_");
  if (raw === "END_OF_PRODUCTION") return "End of Production";
  if (raw === "DISCONTINUED") return "Inactive";
  if (raw === "ACTIVE") return "Active";
  const lower = String(apiStatus || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (lower === "end_of_production") return "End of Production";
  if (lower === "discontinued" || lower === "inactive") return "Inactive";
  if (lower === "end_of_production" || lower === "end of production") return "End of Production";
  return "Active";
}

/**
 * Map API product entity → UI shape used by catalog / detail / form pages.
 * Field names aligned with ProductCatalogCreateRequest / swagger DTO.
 */
export function mapApiProductToUi(api) {
  if (!api || typeof api !== "object") return null;

  const productStatus = firstDefined(api.productStatus, api.product_status, api.status);
  const isActive = firstDefined(api.isActive, api.is_active, api.active);
  const id = firstDefined(api.productId, api.product_id, api.id);

  const primaryVendorId = firstDefined(
    api.primaryVendorId,
    api.primary_vendor_id,
    api.preferredVendor1Id,
    api.preferred_vendor_1_id
  );
  const secondaryVendorId = firstDefined(
    api.secondaryVendorId,
    api.secondary_vendor_id,
    api.preferredVendor2Id,
    api.preferred_vendor_2_id
  );

  const primaryVendorName = firstDefined(
    api.primaryVendorName,
    api.primary_vendor_name,
    api.preferredVendor1,
    api.preferred_vendor_1
  );
  const secondaryVendorName = firstDefined(
    api.secondaryVendorName,
    api.secondary_vendor_name,
    api.preferredVendor2,
    api.preferred_vendor_2
  );

  const metricId = firstDefined(api.metricId, api.metric_id);
  const metricCategory = firstDefined(api.metricCategory, api.metric_category) || "";
  const metricName =
    firstDefined(api.metricName, api.metric_name, api.unitOfMeasure, api.unit_of_measure) || "";

  return {
    id: id != null ? String(id) : undefined,
    productId: id,
    name: firstDefined(api.productName, api.product_name, api.name) || "",
    partNumber: firstDefined(api.partNumber, api.part_number) || "",
    category:
      firstDefined(
        api.productCategory,
        api.product_category,
        api.category,
        api.productServiceName,
        api.product_service_name,
        api.productService?.productServiceName,
        api.productService?.name,
        api.serviceName,
        api.service_name
      ) || "",
    manufacturer:
      firstDefined(api.productBrand, api.product_brand, api.manufacturer, api.manufacturerName) || "",
    status: toUiProductStatus(productStatus, isActive),
    productStatus: toApiProductStatus(productStatus || "Active"),
    isActive: isActive !== false && isActive !== "false",
    metricId: metricId != null ? String(metricId) : "",
    metricCategory,
    unitOfMeasure: metricName || (metricId != null ? String(metricId) : ""),
    listPrice:
      toNumberOrUndefined(firstDefined(api.costPrice, api.cost_price, api.listPrice, api.list_price)) ??
      0,
    unitRate: toNumberOrUndefined(
      firstDefined(api.unitPrice, api.unit_price, api.unitRate, api.unit_rate)
    ),
    moq: toNumberOrUndefined(
      firstDefined(api.minimumOrderQuantity, api.minimum_order_quantity, api.moq, api.minOrderQty)
    ),
    reorderQuantity: toNumberOrUndefined(
      firstDefined(api.reorderQuantity, api.reorder_quantity, api.reorderQty)
    ),
    reorderPoint: toNumberOrUndefined(firstDefined(api.reorderPoint, api.reorder_point)),
    maintenanceStockQuantity: toNumberOrUndefined(
      firstDefined(
        api.minimumStockLevel,
        api.minimum_stock_level,
        api.maintenanceStockQuantity,
        api.maintenance_stock_quantity
      )
    ),
    preferredVendor1Id: primaryVendorId != null ? String(primaryVendorId) : "",
    preferredVendor2Id: secondaryVendorId != null ? String(secondaryVendorId) : "",
    preferredVendor1: primaryVendorName || "",
    preferredVendor2: secondaryVendorName || "",
    abcCategory: firstDefined(api.abcAnalysis, api.abc_analysis, api.abcCategory, api.abc_category) || "",
    description:
      firstDefined(api.productDescription, api.product_description, api.description) || "",
    productServiceId: firstDefined(api.productServiceId, api.product_service_id),
    hsnCode: String(
      firstDefined(api.hsnCode, api.hsn_code, api.hsn, api.productBarcode, api.product_barcode) || ""
    ).replace(/\D/g, "").slice(0, 8),
    productBarcode: firstDefined(api.productBarcode, api.product_barcode) || "",
    currencyCode: firstDefined(api.currencyCode, api.currency_code) || "INR",
    availableStock: toNumberOrUndefined(firstDefined(api.availableStock, api.available_stock)) ?? 0,
    totalStock: toNumberOrUndefined(firstDefined(api.totalStock, api.total_stock)) ?? 0,
    reservedStock: toNumberOrUndefined(firstDefined(api.reservedStock, api.reserved_stock)) ?? 0,
    maintenanceStock: toNumberOrUndefined(firstDefined(api.maintenanceStock, api.maintenance_stock)) ?? 0,
    defectiveStock: toNumberOrUndefined(firstDefined(api.defectiveStock, api.defective_stock)) ?? 0,
    raw: api,
  };
}

/**
 * Map UI form fields → API create/update body (ProductCatalogCreateRequest / UpdateRequest).
 */
export function mapFormToApiPayload(form, { forUpdate = false } = {}) {
  const productStatus = toApiProductStatus(form.status || form.productStatus || "Active");
  const isActive =
    form.isActive !== undefined
      ? !!form.isActive
      : form.status !== "Inactive" && productStatus !== "Discontinued";

  const metricId = toNumberOrNull(form.metricId);
  const metricCategory = form.metricCategory || form.category || form.productCategory || "";
  const hsnDigits = String(form.hsnCode || form.hsn || "")
    .replace(/\D/g, "")
    .slice(0, 8);

  const payload = {
    productName: String(form.name || form.productName || "").trim(),
    partNumber: String(form.partNumber || "").trim(),
    productDescription: form.description ? String(form.description).trim() : null,
    productCategory: form.category || form.productCategory || metricCategory || "",
    productBrand: form.manufacturer || form.productBrand || null,
    // HSN is digits-only (max 8). Sent as hsnCode; also mirrored to productBarcode for backends that use that field.
    hsnCode: hsnDigits || null,
    productBarcode: hsnDigits || form.productBarcode || null,
    metricId,
    metricCategory,
    costPrice: toNumberOrNull(form.listPrice ?? form.costPrice) ?? 0,
    unitPrice: toNumberOrNull(form.unitRate ?? form.unitPrice) ?? 0,
    currencyCode: form.currencyCode || "INR",
    reorderPoint: toNumberOrNull(form.reorderPoint),
    reorderQuantity: toNumberOrNull(form.reorderQuantity),
    minimumOrderQuantity: toNumberOrNull(form.moq ?? form.minimumOrderQuantity),
    minimumStockLevel: toNumberOrNull(form.maintenanceStockQuantity ?? form.minimumStockLevel),
    abcAnalysis: form.abcCategory || form.abcAnalysis || null,
    productStatus,
    primaryVendorId: toNumberOrNull(form.preferredVendor1Id || form.primaryVendorId),
    secondaryVendorId: toNumberOrNull(form.preferredVendor2Id || form.secondaryVendorId),
  };

  const productServiceId = toNumberOrNull(form.productServiceId);
  if (productServiceId != null) {
    payload.productServiceId = productServiceId;
  }

  if (forUpdate) {
    payload.isActive = isActive;
  }

  return payload;
}

function buildPageQuery({ page, size, sortBy, sortDir } = {}) {
  const p = {
    page: page ?? DEFAULT_PAGE.page,
    size: size ?? DEFAULT_PAGE.size,
    sortBy: sortBy ?? DEFAULT_PAGE.sortBy,
    sortDir: sortDir ?? DEFAULT_PAGE.sortDir,
  };
  return `page=${p.page}&size=${p.size}&sortBy=${encodeURIComponent(p.sortBy)}&sortDir=${encodeURIComponent(p.sortDir)}`;
}

function mapPageToUi(page) {
  const content = (page.content || []).map(mapApiProductToUi).filter(Boolean);
  return {
    ...page,
    content,
    products: content,
  };
}

/** productServiceId → display name, loaded once from FSM products-services. */
let productServiceNameCache = null;

async function getProductServiceNameMap() {
  if (productServiceNameCache) return productServiceNameCache;
  try {
    const { categories } = await fetchCategoryDropdownOptions({ size: 100 });
    const map = new Map();
    (categories || []).forEach((c) => {
      const name = c.name || c.label;
      if (!name) return;
      if (c.id != null && c.id !== "") map.set(String(c.id), name);
      if (c.productServiceId != null && c.productServiceId !== "") {
        map.set(String(c.productServiceId), name);
      }
    });
    productServiceNameCache = map;
    return map;
  } catch {
    return new Map();
  }
}

/**
 * Fill blank category labels from productServiceId using the FSM service name map.
 * Keeps productCategory from the API when present — never hardcodes labels.
 */
async function enrichProductsWithCategoryNames(products) {
  const list = products || [];
  const needsLookup = list.some((p) => !p.category && p.productServiceId != null && p.productServiceId !== "");
  if (!needsLookup) return list;

  const map = await getProductServiceNameMap();
  if (!map.size) return list;

  return list.map((p) => {
    if (p.category) return p;
    const name = map.get(String(p.productServiceId));
    return name ? { ...p, category: name } : p;
  });
}

async function enrichProductWithCategoryName(product) {
  if (!product) return product;
  const [enriched] = await enrichProductsWithCategoryNames([product]);
  return enriched;
}

/** GET /product-catalog/search */
export async function searchProducts(params = {}) {
  const query = buildPageQuery(params);
  return dedupeAsync(`searchProducts:${query}`, async () => {
    const response = await imsGet(`/product-catalog/search?${query}`, SEARCH);


  console.log("Raw API Response:", response);

  const page = unwrapPage(response);

  console.log("After unwrapPage:", page);
  console.log("Content:", page.content);

    return mapPageToUi(unwrapPage(response));
  });
}

/** GET /product-catalog/search/{productId} */
export async function fetchProductById(productId) {
  const response = await imsGet(`/product-catalog/search/${productId}`, SEARCH);
  return enrichProductWithCategoryName(mapApiProductToUi(unwrapItem(response)));
}

/** GET /product-catalog/search/name */
export async function searchProductsByName(searchTerm, params = {}) {
  const response = await imsGet(
    `/product-catalog/search/name?searchTerm=${encodeURIComponent(searchTerm)}&${buildPageQuery(params)}`,
    SEARCH
  );
  return mapPageToUi(unwrapPage(response));
}

/** GET /product-catalog/search/part-number */
export async function searchProductsByPartNumber(searchTerm, params = {}) {
  const response = await imsGet(
    `/product-catalog/search/part-number?searchTerm=${encodeURIComponent(searchTerm)}&${buildPageQuery({
      ...params,
      sortBy: params.sortBy || "part_number",
    })}`,
    SEARCH
  );
  return mapPageToUi(unwrapPage(response));
}

/** GET /product-catalog/search/category */
export async function searchProductsByCategory(productCategory, params = {}) {
  const response = await imsGet(
    `/product-catalog/search/category?productCategory=${encodeURIComponent(productCategory)}&${buildPageQuery(params)}`,
    SEARCH
  );
  return mapPageToUi(unwrapPage(response));
}

/** GET /product-catalog/search/abc-analysis */
export async function searchProductsByAbcAnalysis(abcAnalysis, params = {}) {
  const response = await imsGet(
    `/product-catalog/search/abc-analysis?abcAnalysis=${encodeURIComponent(abcAnalysis)}&${buildPageQuery(params)}`,
    SEARCH
  );
  return mapPageToUi(unwrapPage(response));
}

/** GET /product-catalog/search/status?isActive= */
export async function searchProductsByActiveStatus(isActive, params = {}) {
  const response = await imsGet(
    `/product-catalog/search/status?isActive=${!!isActive}&${buildPageQuery(params)}`,
    SEARCH
  );
  return mapPageToUi(unwrapPage(response));
}

/** GET /product-catalog/search/product-status?productStatus= */
export async function searchProductsByProductStatus(productStatus, params = {}) {
  // Swagger examples use lowercase: productStatus=active
  const key = toApiProductStatus(productStatus);
  const statusParam = String(key || "Active").toLowerCase();
  const response = await imsGet(
    `/product-catalog/search/product-status?productStatus=${encodeURIComponent(statusParam)}&${buildPageQuery(params)}`,
    SEARCH
  );
  return mapPageToUi(unwrapPage(response));
}

/** GET /product-catalog/search/primary-vendor/{vendorId} */
export async function searchProductsByPrimaryVendor(vendorId, params = {}) {
  const response = await imsGet(
    `/product-catalog/search/primary-vendor/${vendorId}?${buildPageQuery(params)}`,
    SEARCH
  );
  return mapPageToUi(unwrapPage(response));
}

/** GET /product-catalog/search/secondary-vendor/{vendorId} */
export async function searchProductsBySecondaryVendor(vendorId, params = {}) {
  const response = await imsGet(
    `/product-catalog/search/secondary-vendor/${vendorId}?${buildPageQuery(params)}`,
    SEARCH
  );
  return mapPageToUi(unwrapPage(response));
}

/** GET /product-catalog/search/service/{productServiceId} */
export async function searchProductsByService(productServiceId, params = {}) {
  const response = await imsGet(
    `/product-catalog/search/service/${productServiceId}?${buildPageQuery(params)}`,
    SEARCH
  );
  return mapPageToUi(unwrapPage(response));
}

/** GET /product-catalog/count */
export async function fetchProductCount() {
  return dedupeAsync("productCount", async () => {
    const response = await imsGet("/product-catalog/count", SEARCH);
    return extractCount(response);
  });
}

/** POST /product-catalog/add */
export async function createProduct(form) {
  const body = mapFormToApiPayload(form, { forUpdate: false });
  if (body.productServiceId == null) {
    // productServiceId is required by swagger; resolve from FSM products-services if available.
    try {
      const { searchProductServices } = await import("./fsmService");
      const systems = await searchProductServices({ page: 0, size: 10 });
      const list = Array.isArray(systems) ? systems : systems?.content || [];
      const first = list[0];
      const sid = first?.productServiceId ?? first?.id;
      if (sid != null) body.productServiceId = Number(sid);
    } catch {
      /* leave unset — API will return a clear validation error */
    }
  }
  const response = await imsPost("/product-catalog/add", body, ADD);
  const item = unwrapItem(response);
  const mapped = mapApiProductToUi(item);
  if (mapped?.id) return mapped;
  const fallbackId = firstDefined(item?.productId, item?.product_id, item?.id);
  return (
    mapApiProductToUi({ ...body, productId: fallbackId }) || {
      id: fallbackId != null ? String(fallbackId) : undefined,
      ...form,
    }
  );
}

/** PUT /product-catalog/update/{productId} */
export async function updateProduct(productId, form) {
  const body = mapFormToApiPayload(form, { forUpdate: true });
  const response = await imsPut(`/product-catalog/update/${productId}`, body, UPDATE);
  const mapped = mapApiProductToUi(unwrapItem(response));
  if (mapped?.id) return mapped;
  return mapApiProductToUi({ ...body, productId }) || { id: String(productId), ...form };
}

/**
 * Product Catalog list loader.
 * Uses GET /product-catalog/search by default, or
 * GET /product-catalog/search/service/{productServiceId} when a category is selected.
 */
export async function loadProductCatalog({
  searchQuery = "",
  filterCategory = "ALL",
  filterProductServiceId = "ALL",
  filterStatus = "ALL",
  filterManufacturer = "ALL",
  page = 0,
  size = 10,
} = {}) {
  const dedupeKey = [
    "loadCatalog",
    searchQuery,
    filterCategory,
    filterProductServiceId,
    filterStatus,
    filterManufacturer,
    page,
    size,
  ].join("|");

  return dedupeAsync(dedupeKey, () =>
    loadProductCatalogUncached({
      searchQuery,
      filterCategory,
      filterProductServiceId,
      filterStatus,
      filterManufacturer,
      page,
      size,
    })
  );
}

async function loadProductCatalogUncached({
  searchQuery = "",
  filterCategory = "ALL",
  filterProductServiceId = "ALL",
  filterStatus = "ALL",
  filterManufacturer = "ALL",
  page = 0,
  size = 10,
} = {}) {
  const params = { page, size, sortBy: "productName", sortDir: "ASC" };

  // Category filter → GET /product-catalog/search/service/{productServiceId}
  const serviceId =
    filterProductServiceId !== "ALL" && filterProductServiceId
      ? filterProductServiceId
      : /^\d+$/.test(String(filterCategory || "").trim())
        ? String(filterCategory).trim()
        : null;

  let result;
  if (serviceId) {
    result = await searchProductsByService(serviceId, params);
  } else {
    result = await searchProducts(params);
  }

  let products = result.content || result.products || [];

  // Resolve blank category from productServiceId → FSM productServiceName
  // before any name-based category filtering / display.
  products = await enrichProductsWithCategoryNames(products);

  const q = searchQuery.trim().toLowerCase();
  if (q) {
    products = products.filter((p) => {
      const name = String(p.name || "").toLowerCase();
      const part = String(p.partNumber || "").toLowerCase();
      return name.includes(q) || part.includes(q);
    });
  }

  // Name-based category filter (legacy) when no productServiceId was used.
  if (!serviceId && filterCategory !== "ALL" && filterCategory) {
    products = products.filter(
      (p) => String(p.category || "").toLowerCase() === String(filterCategory).toLowerCase()
    );
  }

  if (filterManufacturer !== "ALL" && filterManufacturer) {
    products = products.filter(
      (p) => String(p.manufacturer || "").toLowerCase() === String(filterManufacturer).toLowerCase()
    );
  }

  if (filterStatus !== "ALL" && filterStatus) {
    products = products.filter((p) => p.status === filterStatus);
  }

  return {
    ...result,
    content: products,
    products,
  };
}

const ABC_GRADE_LABELS = {
  A: "Grade A - High value, tight control, frequent reviews",
  B: "Grade B - Moderate value, moderate control, periodic reviews",
  C: "Grade C - Low value, simple control, bulk ordering",
};

/** Status values for GET /product-catalog/search/product-status (swagger: active, …). */
const PRODUCT_STATUS_PROBE = ["active", "end_of_production", "discontinued"];

function vendorOptionFromVendor(v) {
  if (v?.id == null) return null;
  const id = String(v.id);
  const name = v.tradeName || v.name || `Vendor #${id}`;
  return { id, name, label: name, type: v.type || "" };
}

/** Resolve a productServiceId for GET /search/service/{id} (required path param). */
export async function resolveProductServiceId(explicitId) {
  if (explicitId != null && explicitId !== "") return String(explicitId);
  try {
    const { searchProductServices } = await import("./fsmService");
    const systems = await searchProductServices({ page: 0, size: 10, sortBy: "productServiceId", sortDir: "ASC" });
    const list = Array.isArray(systems) ? systems : systems?.content || [];
    const first = list[0];
    const sid = first?.productServiceId ?? first?.id;
    if (sid != null) return String(sid);
  } catch {
    /* fall through */
  }
  return "1";
}

/**
 * Category dropdown options via GET /saas/fsm/products-services/search
 * Options: productServiceName (label) / productServiceId (value)
 */
export async function fetchCategoryDropdownOptions({ size = 100 } = {}) {
  return dedupeAsync(`fsmProductServices:${size}`, async () => {
    const { searchProductServices } = await import("./fsmService");
    const list = await searchProductServices({
      page: 0,
      size,
      sortBy: "productServiceId",
      sortDir: "ASC",
    });

    const categories = (Array.isArray(list) ? list : [])
      .map((s) => {
        const id = s.id != null ? String(s.id) : s.productServiceId != null ? String(s.productServiceId) : "";
        const name = s.name || s.productServiceName || (id ? `Service #${id}` : "");
        if (!id || !name) return null;
        return {
          id,
          productServiceId: s.productServiceId ?? Number(id),
          name,
          label: name,
        };
      })
      .filter(Boolean)
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));

    return {
      categories,
      productServiceId: categories[0]?.id || null,
    };
  });
}

/** @deprecated */
export async function fetchDistinctProductCategories(opts) {
  const result = await fetchCategoryDropdownOptions(opts);
  return (result.categories || []).map((c) => c.name);
}

/**
 * Part-number suggestions via GET /product-catalog/search/part-number.
 */
export async function searchPartNumberSuggestions(searchTerm, params = {}) {
  const term = String(searchTerm || "").trim();
  if (term.length < 1) return [];
  const page = await searchProductsByPartNumber(term, {
    page: 0,
    size: params.size ?? 10,
    sortBy: "part_number",
    sortDir: "ASC",
  });
  const seen = new Set();
  const options = [];
  (page.products || []).forEach((p) => {
    const pn = String(p.partNumber || "").trim();
    if (!pn || seen.has(pn)) return;
    seen.add(pn);
    options.push({
      partNumber: pn,
      name: p.name || "",
      id: p.id,
      category: p.category || "",
    });
  });
  return options;
}

/**
 * Product Status options via GET /product-catalog/search/product-status?productStatus=active|…
 */
export async function fetchProductStatusDropdownOptions({ size = 10 } = {}) {
  return dedupeAsync(`productStatusDropdown:${size}`, async () => {
    const byValue = new Map();

    await Promise.all(
      PRODUCT_STATUS_PROBE.map(async (probe) => {
        try {
          const page = await searchProductsByProductStatus(probe, {
            page: 0,
            size,
            sortBy: "product_name",
            sortDir: "ASC",
          });
          const products = page.products || [];
          const ui = toUiProductStatus(probe);
          if (ui && ui !== "Inactive") {
            byValue.set(ui, { value: ui, label: ui });
          }
          products.forEach((p) => {
            const fromProduct = p.status;
            if (fromProduct && fromProduct !== "Inactive") {
              byValue.set(fromProduct, { value: fromProduct, label: fromProduct });
            }
          });
        } catch {
          /* skip unsupported status */
        }
      })
    );

    const list = Array.from(byValue.values());
    list.sort((a, b) => {
      if (a.value === "Active") return -1;
      if (b.value === "Active") return 1;
      return a.label.localeCompare(b.label);
    });
    return list;
  });
}

/**
 * Preferred Vendor dropdown options via GET /saas/ims/product-vendors/search
 * (same list for Preferred Vendor 1 and 2 — no hardcoded/mock values).
 */
export async function fetchVendorDropdownOptionsForProduct({ size = 100 } = {}) {
  return dedupeAsync(`productVendorDropdown:${size}`, async () => {
    const { searchVendors } = await import("./productVendorService");
    const page = await searchVendors({
      page: 0,
      size,
      sortBy: "vendorId",
      sortDir: "ASC",
    });
    const byId = new Map();
    (page.vendors || page.content || []).forEach((v) => {
      const opt = vendorOptionFromVendor(v);
      if (opt) byId.set(opt.id, opt);
    });
    return Array.from(byId.values()).sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""))
    );
  });
}

/**
 * Preferred Vendor 1 — GET /product-vendors/search
 * @deprecated Prefer fetchVendorDropdownOptionsForProduct
 */
export async function fetchPrimaryVendorDropdownOptions(opts = {}) {
  return fetchVendorDropdownOptionsForProduct(opts);
}

/**
 * Preferred Vendor 2 — same vendor search API as Vendor 1
 * @deprecated Prefer fetchVendorDropdownOptionsForProduct
 */
export async function fetchSecondaryVendorDropdownOptions(opts = {}) {
  return fetchVendorDropdownOptionsForProduct(opts);
}

/** @deprecated alias */
export async function fetchVendorDropdownOptions(opts) {
  return fetchVendorDropdownOptionsForProduct(opts);
}

/**
 * Product List Category filter options.
 * Seeds productServiceIds from FSM, then confirms/labels via
 * GET /product-catalog/search/service/{productServiceId}?sortBy=productName.
 */
export async function fetchProductListCategoryFilterOptions({ size = 10 } = {}) {
  return dedupeAsync(`listCategoryFilter:${size}`, async () => {
    const { categories } = await fetchCategoryDropdownOptions({ size: 100 });
    const byId = new Map();

    await Promise.allSettled(
      (categories || []).map(async (c) => {
        const sid = c.productServiceId ?? c.id;
        if (sid == null || sid === "") return;
        let label = c.name || c.label || `Service #${sid}`;
        try {
          const page = await searchProductsByService(sid, {
            page: 0,
            size,
            sortBy: "productName",
            sortDir: "ASC",
          });
          const fromProduct = (page.products || [])
            .map((p) => p.category)
            .find(Boolean);
          if (fromProduct) label = fromProduct;
        } catch {
          /* keep FSM label */
        }
        byId.set(String(sid), {
          value: String(sid),
          label,
          name: label,
          productServiceId: sid,
          id: String(sid),
        });
      })
    );

    return Array.from(byId.values()).sort((a, b) =>
      String(a.label).localeCompare(String(b.label))
    );
  });
}

/**
 * ABC Analysis via GET /product-catalog/search/abc-analysis.
 */
export async function fetchAbcAnalysisDropdownOptions({ size = 10 } = {}) {
  return dedupeAsync(`abcDropdown:${size}`, async () => {
    const grades = ["A", "B", "C"];
    const options = [];

    await Promise.all(
      grades.map(async (g) => {
        try {
          await searchProductsByAbcAnalysis(g, {
            page: 0,
            size,
            sortBy: "product_name",
            sortDir: "ASC",
          });
          options.push({
            value: g,
            label: ABC_GRADE_LABELS[g] || `Grade ${g}`,
            available: true,
          });
        } catch {
          /* omit unsupported grade */
        }
      })
    );

    return options.sort((a, b) => a.value.localeCompare(b.value));
  });
}

export async function fetchManufacturerDropdownOptions({ size = 100 } = {}) {
  const { fetchManufacturerVendorsForDropdown } = await import("./productVendorService");
  const vendors = await fetchManufacturerVendorsForDropdown({ size });
  return (vendors || [])
    .map((v) => v.tradeName || v.name)
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
}

/** GET /product-metrics/categories */
export async function fetchMetricCategories() {
  return dedupeAsync("metricCategories", async () => {
    const response = await imsGet("/product-metrics/categories", SEARCH);
    const data = unwrapData(response);
    if (Array.isArray(data)) {
      return data.map((c) => String(c)).filter(Boolean).sort((a, b) => a.localeCompare(b));
    }
    if (Array.isArray(data?.content)) {
      return data.content.map((c) => String(c)).filter(Boolean).sort((a, b) => a.localeCompare(b));
    }
    if (Array.isArray(data?.categories)) {
      return data.categories.map((c) => String(c)).filter(Boolean).sort((a, b) => a.localeCompare(b));
    }
    return [];
  });
}

/** Metrics under one metric category — GET /product-metrics/search/category */
export async function fetchUomOptionsByCategory(metricCategory, params = {}) {
  if (!metricCategory) return [];
  return fetchMetricsByCategory(metricCategory, {
    page: 0,
    size: params.size ?? 100,
    sortBy: params.sortBy || "name",
    sortDir: params.sortDir || "ASC",
  });
}

export async function fetchUomDropdownOptions({ size = 100 } = {}) {
  return fetchMetricsByStatus(true, { page: 0, size });
}

/**
 * Batched Add/Edit Product form lookups.
 * Category → GET /saas/fsm/products-services/search
 * Status → GET /product-catalog/search/product-status
 * Vendors → GET /saas/ims/product-vendors/search
 * (Part number suggestions are loaded on typing via search/part-number.)
 */
export async function fetchAddProductFormLookups({ size = 100 } = {}) {
  return dedupeAsync(`addProductLookups:${size}`, async () => {
    const settled = await Promise.allSettled([
      fetchCategoryDropdownOptions({ size }),
      fetchProductStatusDropdownOptions({ size: 10 }),
      fetchAbcAnalysisDropdownOptions({ size: 10 }),
      fetchMetricCategories(),
      fetchVendorDropdownOptionsForProduct({ size }),
    ]);

    const categoryResult =
      settled[0].status === "fulfilled"
        ? settled[0].value
        : { productServiceId: null, categories: [] };
    const statusOptions = settled[1].status === "fulfilled" ? settled[1].value : [];
    const abcOptions = settled[2].status === "fulfilled" ? settled[2].value : [];
    const metricCategories = settled[3].status === "fulfilled" ? settled[3].value : [];
    const vendors = settled[4].status === "fulfilled" ? settled[4].value : [];

    return {
      categories: categoryResult.categories || [],
      statusOptions: statusOptions || [],
      abcOptions: abcOptions || [],
      metricCategories: metricCategories || [],
      primaryVendors: vendors || [],
      secondaryVendors: vendors || [],
      vendors: vendors || [],
      errors: settled
        .map((r, i) => (r.status === "rejected" ? r.reason?.message || `Lookup ${i} failed` : null))
        .filter(Boolean),
    };
  });
}

/* ─── Product Metrics lookups (IMS04) ─── */

function mapApiMetricToUi(api) {
  if (!api || typeof api !== "object") return null;
  const id = api.metricId ?? api.metric_id ?? api.id;
  const name = api.name ?? api.metricName ?? "";
  const abbreviation = api.abbreviation ?? api.abbr ?? "";
  const category = api.category ?? api.metricCategory ?? api.metric_category ?? "";
  return {
    id: id != null ? String(id) : undefined,
    metricId: id,
    name,
    abbreviation,
    category,
    isActive: api.isActive !== false && api.is_active !== false,
    label: abbreviation ? `${name} (${abbreviation})` : name || `Metric #${id}`,
    raw: api,
  };
}

function buildMetricPageQuery({ page = 0, size = 100, sortBy = "name", sortDir = "ASC" } = {}) {
  return `page=${page}&size=${size}&sortBy=${encodeURIComponent(sortBy)}&sortDir=${encodeURIComponent(sortDir)}`;
}

/** GET /product-metrics/search/status?isActive= */
export async function fetchMetricsByStatus(isActive = true, params = {}) {
  const qs = buildMetricPageQuery(params);
  return dedupeAsync(`metricsByStatus:${isActive}:${qs}`, async () => {
    const response = await imsGet(
      `/product-metrics/search/status?isActive=${!!isActive}&${qs}`,
      SEARCH
    );
    return (unwrapPage(response).content || []).map(mapApiMetricToUi).filter(Boolean);
  });
}

/** GET /product-metrics/search/category?category= */
export async function fetchMetricsByCategory(category, params = {}) {
  const qs = buildMetricPageQuery(params);
  return dedupeAsync(`metricsByCategory:${category}:${qs}`, async () => {
    const response = await imsGet(
      `/product-metrics/search/category?category=${encodeURIComponent(category)}&${qs}`,
      SEARCH
    );
    return (unwrapPage(response).content || []).map(mapApiMetricToUi).filter(Boolean);
  });
}
